/**
 * Copy text to the clipboard, with a fallback for the cases where the
 * async Clipboard API is unavailable or refused.
 *
 * `navigator.clipboard.writeText` is the right default, but it fails in two
 * ways that both surface to the user as a dead button:
 *
 *   1. It is UNDEFINED on a non-secure origin — which is exactly how staging
 *      is served over plain HTTP on a LAN IP. Reading `.writeText` off
 *      `undefined` throws SYNCHRONOUSLY, so a promise-only `.catch()` never
 *      sees it.
 *   2. On a perfectly good HTTPS origin it can still REJECT with
 *      `NotAllowedError: Write permission denied` / `Document is not focused`
 *      — the async API goes through a permission check that a Windows
 *      clipboard manager, an RDP session or an unfocused window can fail.
 *      Reproduced on a real click, focused page, secure context (QA: "copy
 *      button not changing to copied", after a previous report where the same
 *      button worked — the intermittency is the tell).
 *
 * The fallback is the legacy `document.execCommand('copy')` over an offscreen
 * textarea. It matters because it does NOT go through the Permissions API — it
 * rides the transient user activation of the click that is already in progress.
 * Verified to succeed in the very case where `writeText` was denied.
 *
 * MUST be called synchronously from a user gesture (a click handler), or the
 * fallback has no activation to ride and will return false.
 *
 * Returns true when the text reached the clipboard by EITHER route, false when
 * both failed. Never throws — callers decide what to show. The underlying
 * error is logged rather than swallowed: the previous bare `catch {}` made this
 * exact failure impossible to diagnose from a user's report.
 */
export async function copyText(text) {
  const value = text ?? ''

  // 1. Preferred path.
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value)
      return true
    }
  } catch (err) {
    // Not fatal — fall through to the gesture-based path below.
    console.warn('clipboard: writeText failed, trying execCommand', err)
  }

  // 2. Fallback. `readonly` + offscreen rather than `display:none`, because a
  //    hidden or unrendered textarea cannot be selected, and a visible one
  //    would flash and steal focus. `opacity:0` with a 1px box keeps it
  //    selectable while invisible.
  try {
    const ta = document.createElement('textarea')
    ta.value = value
    ta.setAttribute('readonly', '')
    ta.style.cssText =
      'position:fixed;top:0;left:0;width:1px;height:1px;padding:0;border:0;opacity:0'
    document.body.appendChild(ta)
    // Preserve the caller's focus: `select()` moves it to the textarea, so put
    // it back once the copy is done or the field the user was in loses focus.
    const previouslyFocused = document.activeElement
    ta.select()
    ta.setSelectionRange(0, value.length)
    const ok = document.execCommand('copy')
    ta.remove()
    if (previouslyFocused instanceof HTMLElement) previouslyFocused.focus()
    return ok
  } catch (err) {
    console.error('clipboard: execCommand fallback failed', err)
    return false
  }
}
